import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AGENT_CATALOG, AGENT_OBJECT_ID, anchorFamilyOf, isKnownAnchor } from './agent-catalog';
import { AgentRefreshService } from './agent-refresh.service';
import { AgentSpotlightService, SpotlightStep } from './agent-spotlight.service';
import {
  AI_SETTINGS_SECTIONS,
  ANCHORED_SETTINGS,
  SECTIONS_ELSEWHERE,
} from '../pages/tutors/tutor-id/ai-settings/ai-settings.sections';

/**
 * Executes the agent's UI actions in the browser — and only the harmless ones.
 *
 * Every action crosses a trust boundary (it comes from a model), so each is
 * checked again here against the UI catalog, whatever the server checked:
 * page ids, route parameters, tabs, anchors and targets must be known; no CSS
 * selector or URL is ever taken from the agent. Unknown actions do nothing.
 *
 *   navigate  { page, params?, tab?, highlight? }
 *   highlight { anchor | target, note? }
 *   tour      { steps: [{ anchor, title, body }] }
 *   refresh   { resources: [...] }
 *   show      { target, anchor? }   „Zeigen" in the panel: go to the object, then point at it
 *
 * `open_dialog` and `prefill` arrive in phase 3 — ignored until then.
 */

const TARGET = /^[a-z_]+:[A-Za-z0-9_-]{1,64}:[a-z_]+$/;
const TEXT_LIMIT = 400;

function text(value: unknown, limit = TEXT_LIMIT): string {
  return typeof value === 'string' ? value.slice(0, limit) : '';
}

/** The element an anchor or target names, if it is on the page and rendered. */
export function resolveAgentElement(ref: { anchor?: string; target?: string }, root: ParentNode = document): HTMLElement | null {
  let el: Element | null = null;
  if (ref.target) {
    if (!TARGET.test(ref.target)) return null;
    el = root.querySelector(`[data-agent-target="${CSS.escape(ref.target)}"]`);
  } else if (ref.anchor) {
    if (!isKnownAnchor(ref.anchor)) return null;
    const fixed = AGENT_CATALOG.anchors[ref.anchor];
    const family = anchorFamilyOf(ref.anchor);
    if (fixed?.selector) {
      el = root.querySelector(fixed.selector);
    } else if (family && !fixed) {
      const familySelector = AGENT_CATALOG.anchor_families[family.family]?.selector;
      if (familySelector?.includes('{route}')) {
        // nav.<page> / navgroup.<page>: by the link of a page without route parameters
        const route = AGENT_CATALOG.pages[family.value]?.route;
        if (!route || route.includes(':')) return null;
        el = root.querySelector(familySelector.replace('{route}', CSS.escape(route).replace(/\\\//g, '/')));
      } else if (familySelector?.includes('{value}')) {
        // agentmenu.<path> / aisection.<key>: by a link suffix — language-independent
        el = root.querySelector(familySelector.replace('{value}', CSS.escape(family.value)));
      } else {
        el = root.querySelector(`[data-al-anchor="${CSS.escape(ref.anchor)}"]`);
      }
    } else {
      el = root.querySelector(`[data-al-anchor="${CSS.escape(ref.anchor)}"]`);
    }
  }
  return el instanceof HTMLElement && isShown(el) ? el : null;
}

/** Visible on screen: has a box, and no ancestor collapses it to zero height
 *  (nb-menu folds a closed group to height 0 — its links still have rects). */
function isShown(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  if (!el.getClientRects().length || rect.width === 0 || rect.height === 0) return false;
  let node: HTMLElement | null = el.parentElement;
  for (let i = 0; node && i < 8; i++, node = node.parentElement) {
    if (node.getBoundingClientRect().height === 0 && getComputedStyle(node).overflow !== 'visible') return false;
  }
  return true;
}

/** The URL of a catalog page, or null if page, parameters or tab do not fit. */
export function agentPageUrl(pageId: unknown, params: unknown, tab: unknown): string | null {
  if (typeof pageId !== 'string') return null;
  const page = AGENT_CATALOG.pages[pageId];
  if (!page) return null;
  const given = params && typeof params === 'object' ? (params as Record<string, unknown>) : {};
  let route = page.route;
  if (page.tab_param) {
    if (typeof tab === 'string' && (page.tabs ?? []).includes(tab)) {
      route = route.replace(`:${page.tab_param}`, encodeURIComponent(tab));
    } else {
      // every tabbed route also exists without its tab (redirect or default view)
      route = route.replace(new RegExp(`/:${page.tab_param}$`), '');
    }
  }
  const missing: string[] = [];
  route = route.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_m, name: string) => {
    const value = given[name];
    if (typeof value !== 'string' || !AGENT_OBJECT_ID.test(value)) {
      missing.push(name);
      return '';
    }
    return encodeURIComponent(value);
  });
  return missing.length ? null : route;
}

/**
 * The catalog page and tab an agent setting row sits on (`setting.<variable_name>`).
 * Settings are spread over several agent pages: the sections of the AI
 * configuration, some of them now tabs of Lernprozess / Wissen / Einrichtung
 * (SECTIONS_ELSEWHERE). A setting not in any section opens the AI
 * configuration.
 */
export function settingPlace(variable: string | null): { page: string; tab?: string } {
  const has = (v: string) => (s: (typeof AI_SETTINGS_SECTIONS)[number]) => s.tiles.some((t) => t.variables.includes(v));
  const placedBy = variable ? [variable, ...(ANCHORED_SETTINGS[variable] ?? [])] : [];
  const section = placedBy.map((v) => AI_SETTINGS_SECTIONS.find(has(v))).find((s) => !!s);
  if (!section) return { page: 'agent.ai-settings' };
  const elsewhere = SECTIONS_ELSEWHERE[section.key];
  if (!elsewhere) return { page: 'agent.ai-settings', tab: section.key };
  const [path, tab] = elsewhere.split('/');
  for (const [id, page] of Object.entries(AGENT_CATALOG.pages)) {
    if (page.tab_param && page.route === `/tutors/:tutorId/${path}/:${page.tab_param}`) return { page: id, tab };
  }
  // not in the catalog: the AI-configuration URL redirects to the tab
  return { page: 'agent.ai-settings', tab: section.key };
}

@Injectable({ providedIn: 'root' })
export class AgentUiService {
  private readonly router = inject(Router);
  private readonly spotlight = inject(AgentSpotlightService);
  private readonly refresh = inject(AgentRefreshService);
  /** The agent's own navigation still running: a highlight or tour that
   * arrives meanwhile is meant for the target page, so it waits for it
   * (otherwise the page change would close it at once). */
  private pendingNavigation: Promise<unknown> | null = null;

  /** Runs one action. Returns false when it was refused or unknown. */
  handle(raw: unknown): boolean {
    if (!raw || typeof raw !== 'object') return false;
    const action = raw as Record<string, unknown>;
    switch (action['action']) {
      case 'navigate':
        return this.navigate(action);
      case 'highlight':
        return this.highlight(action);
      case 'tour':
        return this.tour(action);
      case 'show':
        return this.show(action);
      case 'refresh':
        return Array.isArray(action['resources'])
          ? this.refresh.emit(action['resources'].filter((r): r is string => typeof r === 'string')).length > 0
          : false;
      default:
        return false;
    }
  }

  private step(raw: Record<string, unknown>, bodyKey: 'note' | 'body'): SpotlightStep | null {
    const anchor = typeof raw['anchor'] === 'string' ? raw['anchor'] : undefined;
    const target = typeof raw['target'] === 'string' ? raw['target'] : undefined;
    if (anchor ? !isKnownAnchor(anchor) : !(target && TARGET.test(target))) return null;
    return { anchor, target: anchor ? undefined : target, title: text(raw['title'], 80) || undefined, body: text(raw[bodyKey]) || undefined };
  }

  private navigate(action: Record<string, unknown>): boolean {
    const url = agentPageUrl(action['page'], action['params'], action['tab']);
    if (!url) return false;
    const highlight = typeof action['highlight'] === 'string' ? action['highlight'] : null;
    const done = this.router.navigateByUrl(url).then((ok) => {
      if (this.pendingNavigation === done) this.pendingNavigation = null;
      if (ok === false || !highlight) return;
      const step = TARGET.test(highlight) ? { target: highlight } : isKnownAnchor(highlight) ? { anchor: highlight } : null;
      if (step) this.spotlight.highlight(step);
    }, () => {
      if (this.pendingNavigation === done) this.pendingNavigation = null;
    });
    this.pendingNavigation = done;
    return true;
  }

  /** Now, or after the agent's navigation that is still running. */
  private onTargetPage(apply: () => void): void {
    if (this.pendingNavigation) this.pendingNavigation.then(apply);
    else apply();
  }

  /**
   * „Zeigen" (bug #212): `bot:<id>:settings` names no element — the rows carry
   * `setting.<variable_name>` and sit on the page of their section. So a
   * settings target goes there first and rings the row; any other target is
   * pointed at where it is.
   */
  private show(action: Record<string, unknown>): boolean {
    const target = typeof action['target'] === 'string' ? action['target'] : '';
    if (!TARGET.test(target)) return false;
    const [type, id, element] = target.split(':');
    const anchor = typeof action['anchor'] === 'string' && action['anchor'].startsWith('setting.') && isKnownAnchor(action['anchor'])
      ? action['anchor']
      : null;
    if (type === 'bot' && (element === 'settings' || anchor)) {
      const place = settingPlace(anchor ? anchor.slice('setting.'.length) : null);
      return this.navigate({ page: place.page, params: { tutorId: id }, tab: place.tab, highlight: anchor ?? undefined });
    }
    return this.highlight({ target });
  }

  private highlight(action: Record<string, unknown>): boolean {
    const step = this.step(action, 'note');
    if (!step) return false;
    this.onTargetPage(() => this.spotlight.highlight(step));
    return true;
  }

  private tour(action: Record<string, unknown>): boolean {
    const steps = Array.isArray(action['steps'])
      ? action['steps']
          .slice(0, 8)
          .map((s) => (s && typeof s === 'object' ? this.step(s as Record<string, unknown>, 'body') : null))
          .filter((s): s is SpotlightStep => s !== null && !!s.anchor)
      : [];
    if (!steps.length) return false;
    this.onTargetPage(() => this.spotlight.tour(steps));
    return true;
  }
}
